/**
 * YouTube Playlist Section
 * Fetches playlist and video details via YouTube Data API v3 and renders
 * a "Watch Next" style layout (main player + sidebar).
 */
(function () {
  'use strict';

  const API_BASE = 'https://www.googleapis.com/youtube/v3';

  /**
   * Parse ISO 8601 duration (e.g. PT10M33S) to MM:SS or HH:MM:SS
   */
  function parseDuration(iso) {
    if (!iso || typeof iso !== 'string') return '0:00';
    const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return '0:00';
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    const parts = [];
    if (hours > 0) parts.push(String(hours).padStart(2, '0'));
    parts.push(String(minutes).padStart(hours > 0 ? 2 : 1, '0'));
    parts.push(String(seconds).padStart(2, '0'));
    return parts.join(':');
  }

  /**
   * Fetch JSON from URL
   */
  async function fetchJson(url) {
    const res = await fetch(url);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error?.message || `HTTP ${res.status}`);
    }
    return res.json();
  }

  /**
   * Initialize a single YouTube playlist section
   */
  function initSection(section) {
    const playlistId = section.dataset.playlistId;
    const apiKey = section.dataset.apiKey;
    const maxVideos = parseInt(section.dataset.maxVideos || '25', 10);

    if (!playlistId || !apiKey) return;

    const loadingEl = section.querySelector('.youtube-playlist__loading');
    const errorEl = section.querySelector('.youtube-playlist__error');
    const playerEl = section.querySelector('.youtube-playlist__player');
    const iframe = section.querySelector('.youtube-playlist__player iframe');
    const titleEl = section.querySelector('.youtube-playlist__title');
    const descEl = section.querySelector('.youtube-playlist__description');
    const listEl = section.querySelector('.youtube-playlist__list');
    const sidebarHeading = section.querySelector('.youtube-playlist__sidebar-heading');

    function showError(msg) {
      loadingEl.classList.add('hidden');
      playerEl.classList.add('hidden');
      errorEl.classList.remove('hidden');
      errorEl.textContent = msg;
    }

    function hideLoading() {
      loadingEl.classList.add('hidden');
    }

    function showPlayer() {
      loadingEl.classList.add('hidden');
      errorEl.classList.add('hidden');
      playerEl.classList.remove('hidden');
    }

    function setActiveVideo(video) {
      iframe.src = `https://www.youtube.com/embed/${video.id}?enablejsapi=1&controls=1&rel=0`;
      iframe.title = video.title;
      titleEl.textContent = video.title;
      descEl.textContent = video.description || '';

      listEl.querySelectorAll('[role="listitem"]').forEach((item) => {
        const isActive = item.dataset.videoId === video.id;
        item.classList.toggle('bg-secondary-background', isActive);
        item.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function truncate(str, len) {
      if (!str) return '';
      if (str.length <= len) return str;
      return str.slice(0, len).trim() + '…';
    }

    function renderSidebar(videos) {
      if (sidebarHeading) {
        sidebarHeading.textContent = `Playlist (${videos.length})`;
      }
      listEl.innerHTML = '';
      videos.forEach((v) => {
        const item = document.createElement('div');
        item.setAttribute('role', 'listitem');
        item.dataset.videoId = v.id;
        item.className =
          'youtube-playlist__item flex cursor-pointer gap-sm p-2xs transition hover:bg-secondary-background';
        item.setAttribute('aria-current', 'false');
        const shortDesc = truncate(v.description || '', 80);
        item.innerHTML = `
          <div class="youtube-playlist__thumb relative aspect-video shrink-0 overflow-hidden bg-secondary-background">
            <img src="${v.thumbnail}" alt="" class="h-full w-full object-cover" loading="lazy" width="144" height="80">
            <span class="youtube-playlist__duration absolute right-1 bottom-1 bg-black/80 px-1 py-0.5 text-xs text-white">${v.duration}</span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="line-clamp-2 text-sm font-semibold">${escapeHtml(v.title)}</div>
            ${shortDesc ? `<div class="mt-0.5 line-clamp-2 text-xs text-secondary-text">${escapeHtml(shortDesc)}</div>` : ''}
          </div>
        `;
        item.tabIndex = 0;
        item.addEventListener('click', () => setActiveVideo(v));
        item.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            setActiveVideo(v);
          }
        });
        listEl.appendChild(item);
      });
    }

    function escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    (async function load() {
      try {
        const [playlistRes, itemsRes] = await Promise.all([
          fetchJson(
            `${API_BASE}/playlists?part=snippet&id=${encodeURIComponent(playlistId)}&key=${encodeURIComponent(apiKey)}`
          ),
          fetchJson(
            `${API_BASE}/playlistItems?part=snippet,contentDetails&playlistId=${encodeURIComponent(playlistId)}&maxResults=${maxVideos}&key=${encodeURIComponent(apiKey)}`
          ),
        ]);

        const items = itemsRes.items || [];

        if (!items.length) {
          showError('No videos found in this playlist.');
          return;
        }

        const videoIds = items
          .map((i) => i.contentDetails?.videoId || i.snippet?.resourceId?.videoId)
          .filter(Boolean);

        if (videoIds.length === 0) {
          showError('Could not load playlist videos.');
          return;
        }

        const videosRes = await fetchJson(
          `${API_BASE}/videos?part=snippet,contentDetails&id=${videoIds.join(',')}&key=${encodeURIComponent(apiKey)}`
        );

        const videosById = {};
        (videosRes.items || []).forEach((v) => {
          videosById[v.id] = {
            id: v.id,
            title: v.snippet?.title || '',
            description: v.snippet?.description || '',
            thumbnail:
              v.snippet?.thumbnails?.medium?.url ||
              v.snippet?.thumbnails?.default?.url ||
              `https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`,
            duration: parseDuration(v.contentDetails?.duration),
          };
        });

        const videos = videoIds.map((id) => videosById[id]).filter(Boolean);

        if (videos.length === 0) {
          showError('Could not load video details.');
          return;
        }

        hideLoading();
        showPlayer();
        renderSidebar(videos);
        setActiveVideo(videos[0]);
      } catch (e) {
        showError(e.message || 'Failed to load playlist.');
      }
    })();
  }

  function init() {
    document.querySelectorAll('.youtube-playlist[data-playlist-id][data-api-key]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector('.youtube-playlist');
      if (section) initSection(section);
    });
  }
})();
